import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Input,
  Select,
} from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { listApps } from "../lib/api/catalog";
import { issueLicense } from "../lib/api/license";
import type { LicenseType } from "../lib/api/types";
import { OrganizationPicker, type PickedOrg } from "../components/OrganizationPicker";
import { DEFAULT_TIER, TIER_OPTIONS, tierLabel } from "../lib/licenseTiers";

const LICENSE_TYPE_OPTIONS: { value: LicenseType; label: string }[] = [
  { value: "LICENSE_TYPE_STANDARD", label: "Standard" },
  { value: "LICENSE_TYPE_DESIGN_PARTNER", label: "Design Partner" },
];

function licenseTypeLabel(t: LicenseType): string {
  return LICENSE_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t;
}

/**
 * Staff: Issue License.
 *
 * This is a real operational tool -- it calls POST /v1/licenses against the
 * live console backend and is the actual way an Agent Native employee
 * grants an org access to an app. Kept fully functional (not stubbed) per
 * the project brief.
 */
export function AdminIssueLicenseRoute() {
  // Optional deep-link from the org detail page: /admin/licenses/issue?organizationId=...
  const search = useSearch({ strict: false }) as { organizationId?: string };

  const [org, setOrg] = useState<PickedOrg | null>(
    search.organizationId ? { id: search.organizationId } : null,
  );
  const [appId, setAppId] = useState("");
  const [tier, setTier] = useState(DEFAULT_TIER);
  const [licenseType, setLicenseType] = useState<LicenseType>("LICENSE_TYPE_STANDARD");
  const [trialEndsAt, setTrialEndsAt] = useState("");

  const appsQuery = useQuery({
    queryKey: ["catalog-apps-for-license"],
    queryFn: () => listApps({ limit: 200, offset: 0 }),
  });
  const apps = appsQuery.data?.apps ?? [];
  const app = apps.find((a) => a.id === appId);
  const appOptions = apps.map((a) => ({ value: a.id, label: a.name }));

  // What was submitted, kept so the success message can name things even after
  // the form is edited.
  const [issued, setIssued] = useState<{ orgName: string; appName: string; tier: string; type: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      issueLicense({
        organizationId: org!.id,
        appId,
        tier,
        licenseType,
        designPartnerTrialEndsAt:
          licenseType === "LICENSE_TYPE_DESIGN_PARTNER" && trialEndsAt
            ? new Date(trialEndsAt).toISOString()
            : undefined,
      }),
    onSuccess: () =>
      setIssued({
        orgName: org?.name ?? org?.id ?? "",
        appName: app?.name ?? appId,
        tier: tierLabel(tier),
        type: licenseTypeLabel(licenseType),
      }),
  });

  const canSubmit = !!org && !!appId && !!tier;
  const tierOption = TIER_OPTIONS.find((t) => t.value === tier);

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-2xl font-semibold">Staff: Issue License</h1>

      {mutation.isSuccess && issued && (
        <Alert variant="success" title="License issued">
          Issued {issued.tier} ({issued.type}) license for {issued.appName} to {issued.orgName}.
          <div className="mt-1 text-sm opacity-80">
            License {mutation.data.license.id}; organization {mutation.data.license.organizationId};
            app {mutation.data.license.appId}.
          </div>
        </Alert>
      )}
      {mutation.isError && (
        <Alert variant="danger" title="Failed to issue license">
          {apiErrorMessage(mutation.error)}
        </Alert>
      )}

      <Card padding="lg">
        <CardBody>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <OrganizationPicker value={org} onChange={setOrg} />

            {appsQuery.isError ? (
              <Alert variant="danger" title="Could not load apps">
                {apiErrorMessage(appsQuery.error)}{" "}
                <button type="button" className="underline" onClick={() => appsQuery.refetch()}>
                  Retry
                </button>
              </Alert>
            ) : (
              <Select
                label="App"
                required
                value={appId}
                disabled={appsQuery.isLoading || apps.length === 0}
                placeholder={
                  appsQuery.isLoading
                    ? "Loading apps..."
                    : apps.length === 0
                      ? "No apps in the catalog"
                      : "Select an app"
                }
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setAppId(e.target.value)}
                options={appOptions}
              />
            )}

            <Select
              label="Tier"
              required
              value={tier}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setTier(e.target.value)}
              options={TIER_OPTIONS.map((t) => ({ value: t.value, label: t.label }))}
              helperText={tierOption?.description}
            />
            <Select
              label="License Type"
              value={licenseType}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setLicenseType(e.target.value as LicenseType)
              }
              options={LICENSE_TYPE_OPTIONS}
            />
            {licenseType === "LICENSE_TYPE_DESIGN_PARTNER" && (
              <Input
                label="Trial Ends At (informational only)"
                type="date"
                value={trialEndsAt}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setTrialEndsAt(e.target.value)}
                helperText="This date is informational only -- converting to a paid design-partner license is always a manual action via the Convert page, never automatic on this date."
              />
            )}

            <p className="text-sm" aria-live="polite">
              {canSubmit ? (
                <>
                  Will issue a <strong>{tierLabel(tier)}</strong> ({licenseTypeLabel(licenseType)}) license for{" "}
                  <strong>{app?.name ?? appId}</strong> to <strong>{org?.name ?? org?.id}</strong>.
                </>
              ) : (
                "Choose an organization and an app to review what will be issued."
              )}
            </p>

            <Button type="submit" loading={mutation.isPending} disabled={!canSubmit}>
              Issue License
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
